import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { FaGlobe, FaFilm, FaTv, FaSearch, FaExchangeAlt } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_API_URL || '';

const TYPE_OPTIONS = [
  { key: 'movie', label: 'Filmes', icon: <FaFilm /> },
  { key: 'series', label: 'Séries', icon: <FaTv /> },
  { key: 'live', label: 'TV ao vivo', icon: <FaGlobe /> }
];

const DomainSwapPage = () => {
  const [oldDomain, setOldDomain] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [types, setTypes] = useState(['movie', 'series', 'live']);
  const [status, setStatus] = useState(null);
  const [previewedFor, setPreviewedFor] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const socketRef = useRef(null);

  useEffect(() => {
    fetchStatus();
    fetchHistory();
  }, []);

  useEffect(() => {
    socketRef.current = io(BACKEND_URL, {
      query: { token: localStorage.getItem('token') }
    });

    socketRef.current.on('domainSwapUpdate', (data) => {
      setStatus(data);
      if (!data.isRunning && data.status === 'completed' && data.mode === 'preview') {
        setPreviewedFor({ oldDomain: data.oldDomain, newDomain: data.newDomain, types: [...data.types].sort() });
      }
      if (!data.isRunning && data.status === 'completed' && data.mode === 'apply') {
        setPreviewedFor(null);
        fetchHistory();
      }
      if (!data.isRunning && data.status === 'error') {
        fetchHistory();
      }
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/domain/status`);
      if (response.data && response.data.isRunning) {
        setStatus(response.data);
      }
    } catch (err) {
      console.error('Erro ao buscar status de troca de domínio:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/domain/history`);
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Erro ao buscar histórico de troca de domínio:', err);
    }
  };

  const toggleType = (key) => {
    setTypes((prev) => (prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]));
  };

  const normalizedTypesKey = (arr) => [...arr].sort().join(',');

  const isCurrentInputPreviewed =
    previewedFor &&
    previewedFor.oldDomain === oldDomain.trim().toLowerCase() &&
    previewedFor.newDomain === newDomain.trim().toLowerCase() &&
    normalizedTypesKey(previewedFor.types) === normalizedTypesKey(types);

  const canPreview = oldDomain.trim() && newDomain.trim() && types.length > 0 && !(status && status.isRunning);
  const canApply =
    isCurrentInputPreviewed &&
    status &&
    status.mode === 'preview' &&
    status.status === 'completed' &&
    !status.isRunning;

  const totalMatched = () => {
    if (!status || !status.perType) return 0;
    return Object.values(status.perType).reduce((sum, t) => sum + (t.matched || 0), 0);
  };

  const handlePreview = async () => {
    setError('');
    setPreviewedFor(null);
    try {
      await axios.post(`${BACKEND_URL}/api/domain/preview`, { oldDomain, newDomain, types });
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao iniciar pré-visualização');
    }
  };

  const handleApply = async () => {
    const matched = totalMatched();
    const typeLabels = types.map((t) => TYPE_OPTIONS.find((o) => o.key === t)?.label).join(', ');
    const confirmed = window.confirm(
      `Confirma a troca de domínio "${oldDomain}" → "${newDomain}"?\n\n` +
      `Tipos: ${typeLabels}\n` +
      `${matched} URL(s) serão alteradas em produção agora.\n\n` +
      `Essa ação afeta o conteúdo servido para os clientes. Não pode ser desfeita automaticamente.`
    );
    if (!confirmed) return;

    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/domain/apply`, { oldDomain, newDomain, types });
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao iniciar troca de domínio');
    }
  };

  const formatHistoryEntry = (entry) => {
    const date = new Date(entry.started_at).toLocaleString('pt-BR');
    const typeLabels = entry.types.split(',').map((t) => TYPE_OPTIONS.find((o) => o.key === t)?.label || t).join(', ');
    let parts = [`${date} — ${entry.old_domain} → ${entry.new_domain} (${typeLabels})`];

    if (entry.results) {
      const resultParts = Object.entries(entry.results).map(([key, r]) => {
        const label = TYPE_OPTIONS.find((o) => o.key === key)?.label || key;
        let text = `${label}: ${r.updated} atualizado(s)`;
        if (r.errors) text += `, ${r.errors} erro(s)`;
        return text;
      });
      parts.push(resultParts.join(' · '));
    }

    if (entry.status === 'error') {
      parts.push(`ERRO: ${entry.error_message}`);
    } else if (entry.status === 'running') {
      parts.push('Em andamento...');
    }

    return parts.join(' — ');
  };

  return (
    <div className="movie-page">
      <h1 className="movie-page__title">Trocar Domínio</h1>

      {error && <p className="movie-page__error">{error}</p>}

      <div className="movie-page__card">
        <h2 className="movie-page__card-title">
          <FaExchangeAlt /> Configuração
        </h2>
        <p>
          Use quando o provedor mudar de domínio e o conteúdo já cadastrado parar de tocar.
          Isso reescreve a URL de origem (mantendo a porta e o caminho originais) de Filmes,
          Séries e/ou TV ao vivo já existentes no banco — não afeta sincronizações futuras.
        </p>

        <div className="movie-page__form-group">
          <label htmlFor="old-domain">Domínio atual:</label>
          <input
            type="text"
            id="old-domain"
            value={oldDomain}
            onChange={(e) => setOldDomain(e.target.value)}
            placeholder="ex: hubby.cx"
            disabled={status && status.isRunning}
          />
        </div>

        <div className="movie-page__form-group">
          <label htmlFor="new-domain">Novo domínio:</label>
          <input
            type="text"
            id="new-domain"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="ex: hubby-dmca.com"
            disabled={status && status.isRunning}
          />
        </div>

        <div className="movie-page__form-group">
          <label>Aplicar em:</label>
          <div className="movie-page__categories-list">
            {TYPE_OPTIONS.map((opt) => (
              <div key={opt.key} className="movie-page__category-item">
                <input
                  type="checkbox"
                  id={`type-${opt.key}`}
                  checked={types.includes(opt.key)}
                  onChange={() => toggleType(opt.key)}
                  disabled={status && status.isRunning}
                />
                <label htmlFor={`type-${opt.key}`}>{opt.icon} {opt.label}</label>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handlePreview}
          disabled={!canPreview}
          className="movie-page__btn movie-page__btn--primary"
        >
          <FaSearch /> Pré-visualizar
        </button>
        {' '}
        <button
          onClick={handleApply}
          disabled={!canApply}
          className="movie-page__btn movie-page__btn--primary"
        >
          <FaExchangeAlt /> Aplicar Troca
        </button>
        {!canApply && previewedFor === null && (
          <p className="movie-page__stats-card">Faça uma pré-visualização antes de aplicar.</p>
        )}
      </div>

      {status && (status.isRunning || status.status === 'completed' || status.status === 'error') && (
        <div className="movie-page__card">
          <h2 className="movie-page__card-title">
            {status.isRunning ? 'Em andamento' : status.status === 'error' ? 'Erro' : 'Resultado'}
            {' — '}
            {status.mode === 'apply' ? 'Aplicando troca' : 'Pré-visualização'}
            {': '}{status.oldDomain} → {status.newDomain}
          </h2>

          {status.status === 'error' && <p className="movie-page__error">{status.error}</p>}

          {status.types.map((t) => {
            const pt = status.perType[t];
            if (!pt) return null;
            const label = TYPE_OPTIONS.find((o) => o.key === t)?.label || t;
            return (
              <div key={t} className="movie-page__form-group">
                <h3 className="movie-page__subtitle">{label}</h3>
                <div className="movie-page__progress-bar">
                  <div
                    className="movie-page__progress"
                    style={{ width: `${pt.total ? (pt.processed / pt.total) * 100 : 0}%` }}
                  ></div>
                </div>
                <p>
                  {pt.processed} / {pt.total} verificados — {pt.matched} encontrado(s)
                  {status.mode === 'apply' && ` — ${pt.updated} atualizado(s)`}
                  {pt.errors > 0 && `, ${pt.errors} erro(s)`}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {history.length > 0 && (
        <div className="movie-page__card">
          <h2 className="movie-page__card-title">
            <FaExchangeAlt /> Histórico de Trocas de Domínio
          </h2>
          <div className="movie-page__log-container">
            <ul className="movie-page__log-list">
              {history.map((entry) => (
                <li key={entry.id}>{formatHistoryEntry(entry)}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainSwapPage;
